import type { ReleaseNote } from './types';

export const RELEASE_NOTES: ReleaseNote[] = [
  {
    date: '2026-10-23',
    title: 'Folders on the files page',
    summary: 'Browse files in folders, and keep that structure when you upload.',
    items: [
      { tag: 'new', description: 'Open folders on the files page' },
      {
        tag: 'new',
        description:
          'Move files into a folder, rename a folder, or make a folder from selected files',
      },
      {
        tag: 'improved',
        description: 'Uploading a folder or a zip keeps the folder structure',
      },
    ],
  },
  {
    date: '2026-10-23',
    title: 'Outlook emails and images',
    summary: 'Open an Outlook email, its attachments, and images in the preview.',
    items: [
      {
        tag: 'new',
        description: 'Preview an Outlook email, including the subject, sender and message',
      },
      { tag: 'new', description: 'Open an attachment from that email in the same preview' },
      { tag: 'new', description: 'Preview an image in the document viewer' },
    ],
  },
  {
    date: '2026-10-23',
    title: 'Who else is editing',
    summary: 'See the other people in a working document.',
    items: [{ tag: 'new', description: 'See who else is editing a working document' }],
  },
  {
    date: '2026-10-23',
    title: 'More improvements & fixes',
    items: [
      { tag: 'new', description: 'Open a saved finding full screen' },
      {
        tag: 'improved',
        description: 'A PDF working document has the same page controls as other PDFs',
      },
    ],
  },
  {
    date: '2026-10-20',
    title: 'Blank working documents',
    summary:
      'Start a document or a spreadsheet yourself, and clear out the ones you no longer need.',
    items: [
      {
        tag: 'new',
        description: 'Start a blank document or spreadsheet from working documents',
      },
      {
        tag: 'new',
        description: 'Select working documents and archive, restore or delete them together',
      },
    ],
  },
  {
    date: '2026-10-20',
    title: 'Search in reviews',
    summary: 'Search the review lists from the toolbar.',
    items: [
      {
        tag: 'new',
        description:
          'Search contract review, technical compliance, reservations, bid compliance, extractions and CV analysis',
      },
    ],
  },
  {
    date: '2026-10-16',
    title: 'My tasks',
    summary: 'See what is due across your projects.',
    items: [
      {
        tag: 'new',
        description:
          'My tasks shows what is due today or overdue, assigned to you, or created by you, across projects',
      },
    ],
  },
  {
    date: '2026-10-16',
    title: 'Choose who can open a working document',
    summary: 'Invite specific people, or everyone in the project.',
    items: [
      {
        tag: 'new',
        description:
          'Share a working document with invited people or the whole project, as an editor or a viewer',
      },
    ],
  },
  {
    date: '2026-10-16',
    title: 'Chat where you want it',
    summary: 'Dock chat, pop it out, or open it full screen.',
    items: [
      {
        tag: 'improved',
        description: 'Dock chat on the left or the right, pop it out, or open it full screen',
      },
    ],
  },
  {
    date: '2026-10-16',
    title: 'Voice input',
    summary: 'Dictate a chat message instead of typing it.',
    items: [
      {
        tag: 'new',
        description: 'Dictate a chat message, with the words inserted at the cursor',
      },
    ],
  },
  {
    date: '2026-10-16',
    title: 'Technical proposal requirements',
    summary: 'Bid compliance includes technical proposal requirements.',
    items: [
      { tag: 'improved', description: 'Bid compliance shows technical proposal requirements' },
    ],
  },
  {
    date: '2026-10-16',
    title: 'Working documents at hand',
    summary: 'Pin a working document, and ask Volve to change it from the page.',
    items: [
      {
        tag: 'new',
        description: 'Favourite a working document and open it from the sidebar',
      },
      {
        tag: 'new',
        description: 'Ask Volve to change a working document from the document page',
      },
    ],
  },
  {
    date: '2026-10-13',
    title: 'Tasks and working documents from chat',
    summary: 'Turn an answer, or the text you select, into a task or a working document.',
    items: [
      {
        tag: 'new',
        description:
          'Create a task, or save to working documents, from an answer or from selected text',
      },
      { tag: 'improved', description: 'Start a new thread from the chat header' },
      {
        tag: 'improved',
        description:
          'Chat waits while an attached file is scanned. Retry continues the scan without uploading the file again',
      },
    ],
  },
  {
    date: '2026-10-13',
    title: 'How uploaded files are read',
    summary: 'See which file types you can upload, and how models and drawings are read.',
    items: [
      {
        tag: 'improved',
        description: 'The upload groups accepted types, such as drawings, models and spreadsheets',
      },
      {
        tag: 'new',
        description: 'The files list explains how Revit, IFC and DWG files are read',
      },
    ],
  },
  {
    date: '2026-10-13',
    title: 'Suggested changes on a scope row',
    summary: 'Accept a suggested change on a scope row, or keep the value you wrote.',
    items: [
      {
        tag: 'new',
        description: 'A scope row can offer a suggested change to accept, or keep your value',
      },
    ],
  },
  {
    date: '2026-10-13',
    title: 'More improvements & fixes',
    items: [
      { tag: 'improved', description: 'Next conflict moves to the next passage in the finding' },
      {
        tag: 'new',
        description: 'Copy or download the text you select in a document preview',
      },
    ],
  },
  {
    date: '2026-10-12',
    title: 'Spreadsheet working documents',
    summary: 'Edit a spreadsheet working document in Volve, and open the source behind a cell.',
    items: [
      {
        tag: 'new',
        description: 'Edit a spreadsheet working document in Volve, and it saves as you work',
      },
      { tag: 'new', description: 'Open the source behind a cited cell' },
    ],
  },
  {
    date: '2026-10-12',
    title: 'Standards for a scope run',
    summary:
      'Upload a standard while you start a scope run, and use the tender bill when the tender has one.',
    items: [
      {
        tag: 'new',
        description:
          'Upload a technical standard or specification system from the scope run, and reuse it from your library',
      },
      {
        tag: 'improved',
        description: 'The tender bill is offered only when the tender has a bill of quantities',
      },
    ],
  },
  {
    date: '2026-10-11',
    title: 'Scope breakdown as a sheet',
    summary:
      'Volve writes the scope breakdown as a sheet you can edit, then update when files change.',
    items: [
      { tag: 'new', description: 'Run the scope breakdown as a sheet, and edit its rows' },
      {
        tag: 'improved',
        description: 'Update keeps the sheet, including statuses, comments, tasks and your edits',
      },
      {
        tag: 'improved',
        description: 'A pack waits for the scope breakdown before its scope is filled',
      },
    ],
  },
  {
    date: '2026-10-11',
    title: 'Compliance starts with key information',
    summary: 'When key information is missing, extract it and run compliance in one step.',
    items: [
      {
        tag: 'improved',
        description:
          'Compliance says it needs key information from the tender and the bid, and offers Extract and run compliance',
      },
    ],
  },
  {
    date: '2026-10-09',
    title: 'Uploads, lists and selected text',
    summary: 'Open a folder in an upload, scroll long lists, and keep the text you select.',
    items: [
      {
        tag: 'improved',
        description: 'In an upload, open a folder and see which files failed or need review',
      },
      { tag: 'improved', description: 'Working documents and tasks load as you scroll' },
      {
        tag: 'improved',
        description:
          'Select text in chat or a review to copy it, save it as a finding, or download it',
      },
    ],
  },
  {
    date: '2026-10-08',
    title: 'Incorrect conflicts stay hidden',
    summary:
      'Findings you marked incorrect stay out of the list, and the filter can bring them back.',
    items: [
      {
        tag: 'improved',
        description:
          'Conflict findings marked incorrect are hidden until you show them in the filter',
      },
    ],
  },
  {
    date: '2026-10-07',
    title: 'Tasks, scope and shared working documents',
    summary:
      'Share tasks and working documents with the project, and read a scope row with its quote and comments.',
    sections: [
      {
        title: 'Tasks',
        items: [
          { tag: 'new', description: 'Assign a task to more than one person' },
          { tag: 'new', description: 'Archive a task and restore it later' },
          { tag: 'new', description: 'Select several tasks and archive or restore them together' },
          {
            tag: 'improved',
            description: 'Switch between tasks assigned to you, created by you, and all tasks',
          },
          {
            tag: 'improved',
            description: 'The workspace filter is remembered for each project',
          },
        ],
      },
      {
        title: 'Scope breakdown',
        items: [
          { tag: 'new', description: 'A scope row shows the quote it comes from' },
          { tag: 'new', description: 'Comment on a scope row' },
        ],
      },
      {
        title: 'Working documents',
        items: [
          {
            tag: 'new',
            description:
              'Share a working document with the project, under Yours and Shared with you',
          },
          { tag: 'new', description: 'Open the version history of a working document' },
          {
            tag: 'improved',
            description: 'A working document in a tender or contract opens inside that workspace',
          },
        ],
      },
      {
        title: 'Checklists',
        items: [{ tag: 'new', description: 'Save a checklist item as a finding' }],
      },
      {
        title: 'Navigation',
        items: [{ tag: 'improved', description: 'Back goes to the parent page' }],
      },
    ],
  },
  {
    date: '2026-10-05',
    title: 'Chat files, coverage and learnings',
    summary:
      'Attach a past bid as a zip, save a chat answer, and see why a file or a learning did not finish.',
    sections: [
      {
        title: 'Chat',
        items: [
          {
            tag: 'new',
            description: 'Attach a .zip up to 2 GB, such as a past bid, from a new conversation',
          },
          {
            tag: 'new',
            description: 'Save the latest answer to working documents',
          },
          {
            tag: 'improved',
            description: 'Saved files have a View button, and Download saves the file',
          },
        ],
      },
      {
        title: 'Documents',
        items: [
          {
            tag: 'improved',
            description:
              'The file list shows why a document failed, including a copy-protected PDF',
          },
        ],
      },
      {
        title: 'Subcontractor coverage',
        items: [
          {
            tag: 'improved',
            description: 'Requirements outside a quote’s trade sit in their own group',
          },
          { tag: 'improved', description: 'The headline counts what the quote offered' },
        ],
      },
      {
        title: 'Learnings',
        items: [
          {
            tag: 'improved',
            description: 'A past tender that did not finish can be retried',
          },
        ],
      },
      {
        title: 'Scope breakdown',
        items: [
          {
            tag: 'improved',
            description: 'Re-run analysis when a scope result is out of date',
          },
        ],
      },
      {
        title: 'Working documents',
        items: [{ tag: 'new', description: 'The working documents link shows how many there are' }],
      },
    ],
  },
  {
    date: '2026-10-01',
    title: 'Saved findings in a table',
    summary: 'Scan saved findings in a table, open the detail, and see which finding each one is.',
    items: [
      { tag: 'new', description: 'Saved findings open as a table with the detail beside the row' },
      { tag: 'new', description: 'Each saved finding shows its source and finding number' },
      { tag: 'improved', description: 'The detail shows headings, lists and emphasis' },
    ],
  },
  {
    date: '2026-10-01',
    title: 'Tasks on the finding',
    summary: 'Open the task behind a finding, and split the list between all tasks and yours.',
    items: [
      { tag: 'new', description: 'Open the linked task from a finding or the review menu' },
      { tag: 'improved', description: 'The task list has tabs for all tasks and your tasks' },
    ],
  },
  {
    date: '2026-10-01',
    title: 'Bid comparison columns',
    summary:
      'Change a column from its menu, add several analysis columns at once, and ask chat about the sheet you are viewing.',
    items: [
      { tag: 'new', description: 'Each comparison column has a menu for its actions' },
      { tag: 'new', description: 'Add several analysis columns in one step' },
      {
        tag: 'new',
        description: 'Chat uses the sheet you are viewing, and the tender it belongs to',
      },
    ],
  },
  {
    date: '2026-10-01',
    title: 'Scope with its evidence',
    summary:
      'See the evidence and sources on a scope item, and keep the last result while a new run finishes.',
    items: [
      {
        tag: 'new',
        description: 'Scope items show the evidence and citations behind them',
      },
      {
        tag: 'improved',
        description: 'Scope shows its own readiness, separate from other analysis',
      },
      {
        tag: 'improved',
        description: 'The previous scope result stays visible while a new run is in progress',
      },
    ],
  },
  {
    date: '2026-10-01',
    title: 'Compliance score for the run',
    summary: 'Read a clearer bid score for the compliance run you opened.',
    items: [
      { tag: 'improved', description: 'A compliance review shows the bid score for that run' },
    ],
  },
  {
    date: '2026-10-01',
    title: 'Unassessed coverage',
    summary: 'Tell a requirement that was never assessed apart from one the offer does not cover.',
    items: [
      {
        tag: 'improved',
        description: 'Unassessed coverage is shown apart from a negative result',
      },
    ],
  },
  {
    date: '2026-10-01',
    title: 'Workspace working documents',
    summary: 'Open the working documents for the workspace you are in.',
    items: [
      {
        tag: 'new',
        description: 'Open working documents for the tender or contract you are working in',
      },
    ],
  },
  {
    date: '2026-10-01',
    title: 'Extractions from the overview',
    summary: 'Start an extraction from the overview and follow it while it runs.',
    items: [
      {
        tag: 'new',
        description: 'Create or run an extraction from the overview, with progress while it runs',
      },
    ],
  },
  {
    date: '2026-10-01',
    title: 'Files in every chat',
    summary: 'Attach files wherever you chat, including analysis, and upload native Revit models.',
    items: [
      { tag: 'new', description: 'Attach files in chat on every surface, including analysis' },
      { tag: 'new', description: 'Upload native Revit (.rvt) files with your other documents' },
    ],
  },
  {
    date: '2026-10-01',
    title: 'Sources on contract risks',
    summary: 'Open the passage behind a key risk before you sign.',
    items: [
      {
        tag: 'new',
        description: 'Key risks to resolve before signing cite the source passages',
      },
    ],
  },
  {
    date: '2026-09-30',
    title: 'Import from Procore',
    summary: 'Pick Procore files when you upload, alongside SharePoint.',
    items: [
      { tag: 'new', description: 'Choose Procore, next to SharePoint, in the document upload' },
    ],
  },
  {
    date: '2026-09-26',
    title: 'A task when action is required',
    summary: 'Turn a finding that still needs action into a task, from the review you are in.',
    items: [
      {
        tag: 'new',
        description:
          'When a review needs action, create a task from chat, saved findings, compliance or a checklist',
      },
    ],
  },
  {
    date: '2026-09-26',
    title: 'More improvements & fixes',
    items: [
      { tag: 'new', description: 'A checklist link opens on the item it points to' },
      { tag: 'improved', description: 'Search in a PDF runs across the whole document' },
    ],
  },
  {
    date: '2026-09-25',
    title: 'Scores on the analysis card',
    summary:
      'See a bid score on the chat analysis card, and keep clarification answers while you write them.',
    sections: [
      {
        title: 'Chat',
        items: [
          {
            tag: 'new',
            description:
              'Analysis cards show a score ring for bid CV, methodology and submission coverage',
          },
          {
            tag: 'improved',
            description: 'Clarification answers stay as a draft while you fill them in',
          },
        ],
      },
      {
        title: 'Bid comparison',
        items: [
          {
            tag: 'improved',
            description: 'Start an analysis heads the shortcut row, with controls to move along it',
          },
        ],
      },
      {
        title: 'Scope breakdown',
        items: [
          {
            tag: 'improved',
            description: 'The title and column headers explain what they show',
          },
        ],
      },
    ],
  },
  {
    date: '2026-09-25',
    title: 'Chat files & project chat',
    summary:
      'See the files chat creates, chat from the project, and start a client comparison from ready-made prompts.',
    sections: [
      {
        title: 'Chat',
        items: [
          {
            tag: 'new',
            description: 'Files a chat message creates sit together in the thread',
          },
          { tag: 'new', description: 'Images in chat open large so you can look closer' },
          { tag: 'new', description: 'Chat from the project, across its workspaces' },
        ],
      },
      {
        title: 'Documents',
        items: [
          {
            tag: 'improved',
            description: 'Opening an editable document shows preparing while it gets ready',
          },
        ],
      },
      {
        title: 'Bid comparison',
        items: [
          {
            tag: 'new',
            description:
              'Start a client comparison, or a ready-made chat prompt, from a row of shortcuts',
          },
        ],
      },
    ],
  },
  {
    date: '2026-09-24',
    title: 'Project working documents',
    summary:
      'Keep shared documents in the project, and see scope breakdown progress while files are still analyzing.',
    sections: [
      {
        title: 'Working documents',
        items: [
          {
            tag: 'new',
            description:
              'List project working documents, open them from chat, and edit them together',
          },
        ],
      },
      {
        title: 'Scope breakdown',
        items: [
          {
            tag: 'improved',
            description: 'Scope breakdown shows progress while documents are still analyzing',
          },
        ],
      },
    ],
  },
  {
    date: '2026-09-22',
    title: 'Sort ambiguities',
    summary: 'Put the unclear clauses you care about first.',
    items: [{ tag: 'improved', description: 'Sort ambiguities by likelihood or review status' }],
  },
  {
    date: '2026-09-18',
    title: 'Chat you can follow',
    summary: 'See what chat is doing in a shorter stream, and keep running work in the thread.',
    items: [
      { tag: 'improved', description: 'The chat activity stream is shorter and easier to scan' },
      {
        tag: 'new',
        description: 'Analysis and other running work stay as cards in the chat thread',
      },
    ],
  },
  {
    date: '2026-09-18',
    title: 'Bid plans & contract review',
    summary:
      'Check what each section will cover before you draft, and take proposed contract changes with you.',
    sections: [
      {
        title: 'Bid draft',
        items: [
          {
            tag: 'new',
            description: 'The bid plan shows what each section covers and a page budget',
          },
          { tag: 'new', description: 'Answer value adds in the section they belong to' },
        ],
      },
      {
        title: 'Contract review',
        items: [
          { tag: 'new', description: 'See proposed redlines on a contract finding' },
          {
            tag: 'improved',
            description: 'Export contract review to Excel, including proposed redlines',
          },
        ],
      },
    ],
  },
  {
    date: '2026-09-17',
    title: 'Bid submission readiness',
    summary:
      'See what’s missing, unclear and covered before you submit, and polish deliverables with AI help.',
    sections: [
      {
        title: 'Submission readiness',
        items: [
          {
            tag: 'new',
            description:
              'Compliance findings show which workspaces are missing, unclear or covered',
          },
          {
            tag: 'new',
            description:
              'Review and export findings, save the ones to keep, and get AI suggestions to close gaps',
          },
        ],
      },
      {
        title: 'Deliverables',
        items: [
          {
            tag: 'new',
            description: 'Work on deliverable copies with proposed changes highlighted inline',
          },
          { tag: 'new', description: 'Export and finalize the deliverables pack' },
          {
            tag: 'improved',
            description:
              'Assessing a draft attaches it to the current bid as a version instead of creating a new bid',
          },
          {
            tag: 'new',
            description: 'Preview draft and methodology proposals as cards in the editor',
          },
        ],
      },
      {
        title: 'More improvements & fixes',
        items: [
          {
            tag: 'improved',
            description:
              'Creating, editing or retriggering a bid comparison takes you straight to the sheet',
          },
        ],
      },
    ],
  },
  {
    date: '2026-09-09',
    title: 'More improvements & fixes',
    items: [
      {
        tag: 'new',
        description: 'Drag files anywhere to upload them through a full-screen drop overlay',
      },
      {
        tag: 'improved',
        description: 'Drag the chat side panel wider, all the way to full screen',
      },
      {
        tag: 'improved',
        description: 'Links to summaries and saved findings highlight the exact spot they open',
      },
    ],
  },
  {
    date: '2026-09-08',
    title: 'More improvements & fixes',
    items: [
      {
        tag: 'new',
        description: 'Upload files through one shared flow with batch review of duplicates',
      },
      {
        tag: 'improved',
        description: 'Checklist runs show as a table with review and comment columns',
      },
      { tag: 'improved', description: 'Creating or rerunning a checklist opens the new run' },
      {
        tag: 'improved',
        description: 'Bid comparison tables keep their headers visible while you scroll',
      },
      { tag: 'improved', description: 'Sidebar navigation is grouped into Areas and Workspace' },
    ],
  },
  {
    date: '2026-09-07',
    title: 'Create bids first, upload later',
    summary: 'Start a bid before any files are ready.',
    items: [{ tag: 'new', description: 'Create a bid without uploading files first' }],
  },
  {
    date: '2026-09-05',
    title: 'Chat feedback & checklist reviews',
    summary: 'Shape chat answers with feedback and take checklist reviews into your own hands.',
    sections: [
      {
        title: 'Chat',
        items: [
          { tag: 'new', description: 'Agree or disagree with chat answers to give feedback' },
          { tag: 'new', description: 'Export an answer or a whole thread to Word' },
          { tag: 'new', description: 'Start from ready-made question sets in the composer' },
        ],
      },
      {
        title: 'Checklists',
        items: [
          { tag: 'new', description: 'Review checklist results yourself and comment per item' },
          { tag: 'improved', description: 'Richer verdicts with citations you can click through' },
        ],
      },
    ],
  },
  {
    date: '2026-09-03',
    title: 'Client bids & findings',
    summary: 'Client bids and bid comparison are back, plus new ways to capture what you learn.',
    items: [
      {
        tag: 'new',
        description: 'Browse the client bids list and compare bids side by side again',
      },
      {
        tag: 'new',
        description: 'Open Teams links that take you straight to the right place in Volve',
      },
      { tag: 'new', description: 'Save selected chat text to your findings' },
      { tag: 'new', description: 'Give feedback on strengths & weaknesses statements' },
    ],
  },
  {
    date: '2026-08-26',
    title: 'Sharper ambiguity findings',
    summary:
      'Ambiguity detection now focuses on the unclear clauses that matter, so you spend less time dismissing noise.',
    sections: [
      {
        title: 'Analysis',
        items: [
          {
            tag: 'improved',
            description:
              'Ambiguity findings are more precise and less noisy after a rebuilt detection method',
          },
        ],
      },
      {
        title: 'Bids & plans',
        items: [
          {
            tag: 'new',
            description: 'Opening a tender now creates a default bid, so you skip the empty list',
          },
          {
            tag: 'improved',
            description: 'See live task progress while an execution plan generates',
          },
          {
            tag: 'improved',
            description: 'See leftover scope under our disciplines when picking work packages',
          },
          {
            tag: 'improved',
            description: 'The mark-as-submitted dialog shows the last plan version name',
          },
        ],
      },
      {
        title: 'More improvements & fixes',
        items: [
          { tag: 'new', description: 'Delete several templates at once' },
          {
            tag: 'improved',
            description:
              'Completion notifications name the project and workspace, and take you there',
          },
        ],
      },
    ],
  },
  {
    date: '2026-08-25',
    title: 'Checklists & value adds',
    summary:
      'Run reusable verification checklists and answer per-section value adds to strengthen your bid draft.',
    sections: [
      {
        title: 'Checklists',
        items: [
          {
            tag: 'new',
            description:
              'Run reusable checklists against tenders, contracts, bids and work packages',
          },
          {
            tag: 'new',
            description: 'Create and organize personal and organization-wide checklist templates',
          },
          {
            tag: 'new',
            description:
              'Watch live checklist question answering with instant citations and reasoning',
          },
        ],
      },
      {
        title: 'Value adds',
        items: [
          {
            tag: 'new',
            description: 'Answer per-section value-add suggestions in chat while drafting your bid',
          },
        ],
      },
      {
        title: 'More improvements & fixes',
        items: [
          {
            tag: 'new',
            description: 'See drawing sheet citation pills and preview blueprints in chat',
          },
          { tag: 'new', description: 'Recent workspaces list in the organization sidebar' },
          {
            tag: 'new',
            description: 'Browser notifications when analyses and document indexing complete',
          },
          {
            tag: 'new',
            description:
              'Chat streams answers, lets you retry a failed turn, and attach files while you type',
          },
          {
            tag: 'new',
            description: 'Chat can update analysis tables and star saved findings',
          },
          {
            tag: 'new',
            description: 'Start a new execution plan version without uploading files first',
          },
          {
            tag: 'fixed',
            description: 'Invite conflicts can be resent instead of dead-ending',
          },
          { tag: 'improved', description: 'Track file upload progress in the upload dialog' },
          {
            tag: 'improved',
            description: 'Manage and preview suppliers’ files in procurement pack files',
          },
          {
            tag: 'improved',
            description: 'Tender overview organized into tabs with evaluation weighting',
          },
          { tag: 'improved', description: 'Expand extraction findings by clicking the text' },
          {
            tag: 'improved',
            description: 'Live status updates for clause scanner and custom topic extractions',
          },
          {
            tag: 'fixed',
            description: 'Citations now render properly inside markdown table cells',
          },
          {
            tag: 'fixed',
            description: 'Extraction summaries show formatted text instead of raw data',
          },
          { tag: 'fixed', description: 'Homepage project list now updates upon returning' },
        ],
      },
    ],
  },
  {
    date: '2026-08-12',
    title: 'Saved findings',
    summary: 'Keep the findings that matter and reuse them across projects.',
    items: [{ tag: 'new', description: 'Save a finding and reuse it across projects' }],
  },
  {
    date: '2026-08-11',
    title: 'Execution plan sparring',
    summary: 'Polish your execution plan with AI, right in the chat.',
    items: [
      {
        tag: 'new',
        description:
          'Review AI-suggested execution plan edits right in the chat, shown as tracked changes',
      },
      { tag: 'new', description: 'Create execution plan versions straight from an upload' },
    ],
  },
  {
    date: '2026-08-08',
    title: 'More improvements & fixes',
    items: [
      { tag: 'new', description: 'Discuss specific cells in subcontractor coverage tables' },
      { tag: 'new', description: 'See exactly which documents failed to process, and why' },
      { tag: 'new', description: 'Pick a red-flag template when running legal deviations' },
      { tag: 'new', description: 'See the version status of every file' },
      { tag: 'new', description: 'Get warned with a support link when document indexing stalls' },
      {
        tag: 'improved',
        description: 'Scope coverage cells now show outcome and price at a glance',
      },
      { tag: 'improved', description: 'Extraction results show skeleton rows while they load' },
      { tag: 'improved', description: 'Get a nudge when an analysis is out of date' },
      { tag: 'improved', description: 'Failed analyses are clearly marked' },
      { tag: 'improved', description: 'Supplier bid counts on procurement cards' },
      {
        tag: 'improved',
        description: 'Clicking a notification highlights the right coverage cell',
      },
      { tag: 'improved', description: 'Helpful descriptions under page and section headers' },
      {
        tag: 'fixed',
        description: 'Extraction findings no longer collapse right after you expand them',
      },
      { tag: 'fixed', description: 'Award criteria now show even when weights are missing' },
      { tag: 'fixed', description: 'Long chat messages no longer overflow' },
    ],
  },
  {
    date: '2026-07-28',
    title: 'Work packages spreadsheet',
    summary: 'Work packages get a spreadsheet view and a smarter picker.',
    items: [
      { tag: 'new', description: 'Edit work packages in a spreadsheet view' },
      { tag: 'new', description: 'Pick work packages from a chapter tree' },
    ],
  },
  {
    date: '2026-07-27',
    title: 'More improvements & fixes',
    items: [
      { tag: 'new', description: 'Re-run strengths & weaknesses right from the card' },
      { tag: 'new', description: 'See evaluation metrics in the table view' },
      { tag: 'improved', description: 'Watch bid leveling progress live while it runs' },
      { tag: 'improved', description: 'Clearer subcontractor tender wording' },
      { tag: 'improved', description: 'Address field moved to the country step in create project' },
    ],
  },
  {
    date: '2026-07-23',
    title: 'A refreshed chat',
    summary: 'Chat gets a cleaner look with context always visible.',
    items: [
      { tag: 'improved', description: 'Cleaner chat message layout with details on hover' },
      { tag: 'new', description: 'See the context on earlier chat messages' },
      { tag: 'new', description: 'Edit suggested chat prompts before sending' },
      { tag: 'new', description: 'Chat suggestions adapt to your language and context' },
    ],
  },
  {
    date: '2026-07-21',
    title: 'Bid overview tabs',
    summary: 'The bid overview is organised into tabs, with a smoother drafting experience.',
    items: [
      { tag: 'new', description: 'Bid overview organised into tabs' },
      { tag: 'improved', description: 'Smoother bid drafting experience' },
    ],
  },
  {
    date: '2026-07-20',
    title: 'Subcontractor reservations',
    summary: 'Reserve requirements towards subcontractors and keep track of who covers what.',
    items: [
      { tag: 'new', description: 'Reserve requirements towards subcontractors' },
      { tag: 'new', description: 'Preview each subcontractor’s scope of work before diving in' },
    ],
  },
  {
    date: '2026-07-15',
    title: 'Projects & navigation',
    summary: 'Finding your way around gets easier across the board.',
    items: [
      { tag: 'new', description: 'Filter your project list, filters stay put as you navigate' },
      {
        tag: 'new',
        description: 'Take a guided tour of the navigation, replayable from the sidebar',
      },
      { tag: 'new', description: 'Your favourites follow you in the organization sidebar' },
      { tag: 'new', description: 'Nested bid navigation in the project sidebar' },
      { tag: 'improved', description: 'Lists load continuously as you scroll, no more paging' },
      { tag: 'improved', description: 'Sort the document list by any column' },
      {
        tag: 'fixed',
        description: 'Project list updates right away after archive, restore or pin',
      },
    ],
  },
  {
    date: '2026-07-07',
    title: 'Comments with @mentions',
    summary: 'Loop in colleagues by mentioning them, they get an email straight to the discussion.',
    items: [
      { tag: 'new', description: 'Mention colleagues with @ in comments' },
      { tag: 'new', description: 'Comments stay in sync, with email links to the right place' },
      { tag: 'new', description: 'Comment alongside your bid draft in a dedicated rail' },
    ],
  },
  {
    date: '2026-07-03',
    title: 'CV analysis',
    summary: 'Check candidates against required roles and share the results.',
    items: [
      { tag: 'new', description: 'Match candidates against required roles in a dedicated table' },
      { tag: 'new', description: 'Export CV findings to share with your team' },
      { tag: 'fixed', description: 'CV findings no longer show stale results when you navigate' },
    ],
  },
  {
    date: '2026-06-19',
    title: 'More improvements & fixes',
    items: [
      { tag: 'new', description: 'See when an extraction was last run' },
      {
        tag: 'improved',
        description: 'Write together in bid draft and execution plan with live collaboration',
      },
      { tag: 'improved', description: 'Pinned projects stay at the top of your list' },
      { tag: 'improved', description: 'See leveled totals and coverage when comparing offers' },
      { tag: 'fixed', description: 'The search dialog now closes with Esc' },
      { tag: 'fixed', description: 'Ambiguity navigation now respects your active filters' },
    ],
  },
  {
    date: '2026-06-15',
    title: 'Subcontractor bids',
    summary: 'Run the full subcontractor bid flow in Volve.',
    items: [
      { tag: 'new', description: 'Invite and evaluate subcontractor bids in dedicated workspaces' },
      { tag: 'new', description: 'Export draft RFPs to Word' },
      { tag: 'new', description: 'Delete procurement packs' },
    ],
  },
  {
    date: '2026-06-10',
    title: 'Templates & exports',
    items: [
      { tag: 'new', description: 'Create reusable work package templates' },
      {
        tag: 'new',
        description: 'Export technical compliance, ambiguities and conflicts to Excel',
      },
    ],
  },
  {
    date: '2026-06-09',
    title: 'Bid compliance checklist',
    summary: 'Check bids against compliance requirements automatically.',
    items: [
      { tag: 'new', description: 'Check bids against compliance requirements automatically' },
      {
        tag: 'improved',
        description: 'Compliance details are easier to read in the redesigned panel',
      },
      { tag: 'fixed', description: 'Contradicted compliance findings no longer show as missing' },
    ],
  },
  {
    date: '2026-06-04',
    title: 'Execution plan overview',
    summary: 'See the execution plan at a glance.',
    items: [{ tag: 'new', description: 'Execution plan overview on the bid page' }],
  },
  {
    date: '2026-05-29',
    title: 'More improvements & fixes',
    items: [
      { tag: 'new', description: 'Export bid comparison sheets to Excel' },
      { tag: 'new', description: 'Set a status on conflict and ambiguity findings' },
      { tag: 'new', description: 'Review contract review statements and clauses yourself' },
      { tag: 'new', description: 'Preview xlsm and dotx files right in the browser' },
      { tag: 'new', description: 'Help Volve learn from the new sidebar card' },
      { tag: 'improved', description: 'Extractions run noticeably faster' },
      { tag: 'fixed', description: 'Execution plan no longer crashes' },
      { tag: 'fixed', description: 'Files list sorting works as expected' },
      { tag: 'fixed', description: 'Completed tender feedback now shows its status correctly' },
    ],
  },
  {
    date: '2026-05-25',
    title: 'Document hierarchy & categories',
    summary: 'Documents organise themselves with a hierarchy and categories.',
    items: [
      { tag: 'new', description: 'Documents are organised into a hierarchy with categories' },
    ],
  },
  {
    date: '2026-05-15',
    title: 'AI chat on more surfaces',
    summary: 'Ask questions about your analyses wherever you see them.',
    items: [
      { tag: 'new', description: 'Chat with your legal deviations and extractions' },
      { tag: 'new', description: 'Chat with your conflict finder results' },
    ],
  },
  {
    date: '2026-05-13',
    title: 'Execution plan 3.0',
    summary: 'A rebuilt execution plan flow that keeps track of every version.',
    items: [
      { tag: 'new', description: 'Execution plan 3.0 with a rebuilt review flow' },
      { tag: 'new', description: 'Name and keep track of execution plan versions' },
    ],
  },
  {
    date: '2026-04-20',
    title: 'Bid drafting 2.0',
    summary: 'Draft your bid with AI that understands your work packages.',
    items: [
      { tag: 'new', description: 'Bid drafting 2.0 with a smarter writing flow' },
      { tag: 'new', description: 'Chat that understands your work packages while you draft' },
    ],
  },
  {
    date: '2026-04-16',
    title: 'More improvements & fixes',
    items: [
      { tag: 'new', description: 'Edit tender feedback after submitting' },
      { tag: 'new', description: 'Favourite work packages' },
      { tag: 'new', description: 'Comment on work package checklist items' },
      { tag: 'new', description: 'Navigate documents faster with the preview toolbar' },
      { tag: 'new', description: 'Open AI chat full screen on desktop' },
      { tag: 'new', description: 'See the output language in the evaluations list' },
      { tag: 'improved', description: 'Bid comparison remembers your display settings per sheet' },
      { tag: 'improved', description: 'Hear a sound when an analysis completes' },
      { tag: 'fixed', description: 'Knowledge base no longer freezes' },
      { tag: 'fixed', description: 'Zip files now extract correctly in Chrome' },
    ],
  },
  {
    date: '2026-04-08',
    title: 'Contract review drill-down',
    summary: 'Review contracts faster with a table-driven view of every deviation.',
    items: [
      {
        tag: 'new',
        description: 'Drill into legal deviations with a table view and severity filters',
      },
      { tag: 'new', description: 'Translate legal deviations' },
      { tag: 'new', description: 'Resize comment panels to fit how you work' },
      { tag: 'new', description: 'See deviation status and unread comments at a glance' },
      { tag: 'improved', description: 'Sources point to the exact page in the document' },
    ],
  },
  {
    date: '2026-03-31',
    title: 'Files & organization',
    summary: 'Uploads get safer and content gets easier to organise.',
    items: [
      { tag: 'new', description: 'Get warned about duplicate documents before uploading' },
      { tag: 'new', description: 'Upload project scheduling files' },
      { tag: 'new', description: 'Preview Excel files without downloading them' },
      { tag: 'new', description: 'Preview documents while reviewing uploads' },
      { tag: 'new', description: 'Search and filter files by extension or uploader' },
      { tag: 'new', description: 'Guides & References tab for internal handbooks' },
      { tag: 'new', description: 'Organise templates into folders' },
      { tag: 'improved', description: 'Smoother zooming in the PDF preview' },
    ],
  },
  {
    date: '2026-03-27',
    title: 'Notifications inbox',
    summary: 'One inbox for everything that needs your attention.',
    items: [
      { tag: 'new', description: 'Notifications inbox keeps track of mentions and activity' },
      { tag: 'new', description: 'Comment directly on engagements' },
    ],
  },
  {
    date: '2026-03-26',
    title: 'Bid comparison sheets',
    summary: 'Compare bids side by side in dedicated sheets.',
    items: [{ tag: 'new', description: 'Compare bids side by side in dedicated sheets' }],
  },
  {
    date: '2026-03-20',
    title: 'More improvements & fixes',
    items: [
      { tag: 'improved', description: 'Clearer dialog when running analyses' },
      { tag: 'fixed', description: 'Page no longer gets stuck after closing a file' },
      { tag: 'fixed', description: 'Extraction filters no longer reset on their own' },
    ],
  },
  {
    date: '2026-03-16',
    title: 'Legal deviations clustering',
    summary: 'Legal deviations grouped by theme, with the biggest risks front and center.',
    items: [
      { tag: 'new', description: 'Legal deviations grouped into clusters with a domain overview' },
      { tag: 'new', description: 'Compound risk banner highlights the biggest risks' },
      { tag: 'new', description: 'Severity tiers per domain' },
    ],
  },
  {
    date: '2026-03-12',
    title: 'A faster app',
    summary: 'A behind-the-scenes upgrade makes the app noticeably faster.',
    items: [{ tag: 'improved', description: 'Faster load times throughout the app' }],
  },
  {
    date: '2026-02-25',
    title: 'Document previews',
    summary: 'Preview files in place, no download needed.',
    items: [
      { tag: 'new', description: 'Preview PDFs in the app and download when needed' },
      { tag: 'improved', description: 'PDF preview works better on mobile' },
    ],
  },
  {
    date: '2026-02-17',
    title: 'SSO login',
    summary: 'Sign in with your company account.',
    items: [
      { tag: 'new', description: 'Log in with your company’s single sign-on' },
      { tag: 'new', description: 'Reset your password right from the login page' },
    ],
  },
  {
    date: '2026-02-12',
    title: 'Learnings (beta)',
    summary:
      'Learnings at a portfolio and client level are now tracked and available in Volve. Upload your tender feedback to uncover insights and trends, identify strengths and weaknesses in your approach, and optimise your tendering process.',
    items: [
      {
        tag: 'new',
        description: 'See what worked and what didn’t across your portfolio and per client',
      },
      {
        tag: 'new',
        description: 'Upload tender feedback in the knowledge base to build learnings',
      },
    ],
  },
  {
    date: '2026-02-09',
    title: 'Clients & languages',
    items: [
      { tag: 'new', description: 'Manage your clients in one place' },
      { tag: 'new', description: 'Get analyses in Spanish and Portuguese' },
    ],
  },
  {
    date: '2026-02-05',
    title: 'More improvements & fixes',
    items: [
      { tag: 'new', description: 'Save useful chat answers to your findings' },
      { tag: 'new', description: 'Copy or ask AI about anything in the risk overview' },
      { tag: 'new', description: 'More knowledge base types to organise your documents' },
      { tag: 'improved', description: 'Filter AI chat by file type' },
      { tag: 'improved', description: 'See an uploaded/failed summary when uploading files' },
      { tag: 'improved', description: 'A proper not-found page instead of a dead end' },
      { tag: 'fixed', description: 'Chat scrolls along as new messages arrive' },
      { tag: 'fixed', description: 'Upload errors now show the file name' },
    ],
  },
  {
    date: '2026-01-22',
    title: 'More improvements & fixes',
    items: [
      { tag: 'new', description: 'Work packages now available on contracts' },
      { tag: 'new', description: 'Checklist column in work packages' },
      { tag: 'new', description: 'Export extractions to Excel' },
      { tag: 'new', description: 'Search on team pages' },
      { tag: 'fixed', description: 'Chat now knows which bid you’re looking at' },
    ],
  },
  {
    date: '2026-01-17',
    title: 'New interface',
    summary: 'A refreshed look with reworked navigation.',
    items: [
      { tag: 'new', description: 'Redesigned navigation' },
      { tag: 'new', description: 'Welcome dialog introducing the new interface' },
      { tag: 'new', description: 'Mark projects and evaluations as favourites' },
      { tag: 'new', description: 'Follow what’s happening in the activity log' },
      { tag: 'new', description: 'Breadcrumb menus for quicker navigation' },
      { tag: 'new', description: 'Chat with your tender from mobile' },
      { tag: 'improved', description: 'Language selects show country flags' },
    ],
  },
  {
    date: '2026-01-14',
    title: 'Change management',
    summary: 'Track contract changes and their impact in one place.',
    items: [
      { tag: 'new', description: 'Keep track of change requests in a dedicated table' },
      { tag: 'new', description: 'Get an AI assessment of each change' },
    ],
  },
  {
    date: '2026-01-10',
    title: 'Uploads & files',
    items: [
      { tag: 'new', description: 'Upload whole folders at once, zip files unpack automatically' },
      { tag: 'new', description: 'See why a file was rejected' },
      { tag: 'improved', description: 'Upload files up to 200 MB' },
      { tag: 'improved', description: 'Edit knowledge base files in place' },
      { tag: 'fixed', description: 'Clearer errors when a file upload fails' },
    ],
  },
];

export const LATEST_RELEASE_NOTE_DATE =
  RELEASE_NOTES.map((note) => note.date)
    .sort()
    .at(-1) ?? '';
